import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "../config/api.js";
import { get } from "../lib/http.js";
import FancyLoader from "../components/FancyLoader.jsx";
import AuthModal from "./AuthModal.jsx";
import logo from "../assets/logo2.png";
import {
  formatDateRangeFromYmds,
} from "../api/events.js";
import {
  formatDayLabel,
  formatTimeLabel,
  ticketDisplayName,
  ticketDateFromType,
} from "../listYourShow/scheduleHelpers.js";
import {
  coupleTicketBadge,
  isAllDaysTicket,
  isCoupleTicket,
  isGroupTicket,
  isTicketSaleActive,
  personsPerUnit,
  ticketCategoryId,
  ticketSelectionKey,
} from "../lib/ticketCouple.js";

const currencyINR = (n) =>
  typeof n === "number" && Number.isFinite(n)
    ? `₹${n.toLocaleString("en-IN")}`
    : "—";

function formatShortDay(ymd) {
  if (!ymd) return "";
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return formatDayLabel(ymd) || ymd;
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function seatStatus(seats) {
  const n = Number(seats ?? 0);
  if (n <= 0) return "sold";
  if (n < 20) return "fast";
  return "available";
}

function statusCopy(status) {
  if (status === "sold") return "Sold out";
  if (status === "fast") return "Filling fast";
  return "Open";
}

export default function BookTicketsPage() {
  const { id } = useParams();
  const nav = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [pickedDate, setPickedDate] = useState(""); // YYYY-MM-DD or __season__
  const [selectedKey, setSelectedKey] = useState("");
  const [qty, setQty] = useState(0);

  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [nextAfterAuth, setNextAfterAuth] = useState("/");

  const currentUser = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("bb_user"));
      if (!raw) return null;
      return { token: raw?.token, id: raw?.id ?? raw?._id };
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("bb_user") || "null")?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!alive) return;
        setEvent(res?.event || null);
      } catch (e) {
        console.error("book page load error:", e);
        if (alive) setEvent(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  const ticketDates = useMemo(() => {
    const list = Array.isArray(event?.ticketCategories) ? event.ticketCategories : [];
    const dates = [];
    const seen = new Set();
    for (const t of list) {
      if (!isTicketSaleActive(t)) continue;
      const d =
        String(t?.date || "").trim() || ticketDateFromType(t?.type || "") || "";
      if (!d || seen.has(d)) continue;
      if (
        !String(t?.date || "").trim() &&
        isAllDaysTicket(t, event) &&
        !ticketDateFromType(t?.type || "")
      ) {
        continue;
      }
      seen.add(d);
      dates.push(d);
    }
    dates.sort();
    return dates;
  }, [event]);

  const seasonPassTiers = useMemo(() => {
    const list = Array.isArray(event?.ticketCategories) ? event.ticketCategories : [];
    return list.filter((t) => {
      if (!isTicketSaleActive(t)) return false;
      const d =
        String(t?.date || "").trim() || ticketDateFromType(t?.type || "");
      if (d) return false;
      return isAllDaysTicket(t, event);
    });
  }, [event]);

  const seasonPassLabel = useMemo(() => {
    const fromSchedule = (event?.scheduleDays || [])
      .filter((d) => d?.active !== false && d?.date)
      .map((d) => String(d.date).trim())
      .filter(Boolean)
      .sort();
    const ymds = fromSchedule.length ? fromSchedule : ticketDates;
    const range = formatDateRangeFromYmds(ymds);
    return range ? `All dates (${range})` : "All dates — Season pass";
  }, [event, ticketDates]);

  const needsDateStep =
    ticketDates.length > 1 || (ticketDates.length >= 1 && seasonPassTiers.length > 0);

  // Auto-pick when only one path exists
  useEffect(() => {
    if (!event || loading) return;
    if (pickedDate) return;

    if (needsDateStep) {
      setStep(1);
      return;
    }
    if (seasonPassTiers.length > 0 && ticketDates.length === 0) {
      setPickedDate("__season__");
      setStep(2);
      return;
    }
    if (ticketDates.length === 1) {
      setPickedDate(ticketDates[0]);
      setStep(2);
      return;
    }
    // Legacy undated day tickets
    setPickedDate("");
    setStep(2);
  }, [event, loading, needsDateStep, seasonPassTiers.length, ticketDates, pickedDate]);

  const dayPool = useMemo(() => {
    const list = Array.isArray(event?.ticketCategories) ? event.ticketCategories : [];
    if (pickedDate === "__season__") {
      return seasonPassTiers;
    }
    if (!pickedDate) {
      return list.filter((t) => isTicketSaleActive(t));
    }
    return list.filter((t) => {
      if (!isTicketSaleActive(t)) return false;
      if (isAllDaysTicket(t, event) && !String(t?.date || "").trim()) return false;
      const d =
        String(t?.date || "").trim() || ticketDateFromType(t?.type || "");
      return d === pickedDate;
    });
  }, [event, pickedDate, seasonPassTiers]);

  const ticketCards = useMemo(() => {
    return dayPool.map((t, i) => {
      const categoryId = ticketCategoryId(t) || String(i);
      const key = ticketSelectionKey({ ...t, categoryId }, i);
      const seats = Number(t.availableSeats ?? 0);
      const group = isGroupTicket(t);
      const couple = isCoupleTicket(t);
      const ppu = personsPerUnit(t);
      let desc = t.desc || "Ticket";
      if (group) desc = `Group — ${ppu} persons per pack`;
      else if (couple) desc = "Couple — 2 persons per ticket";
      if (pickedDate === "__season__") desc = `${desc} · Season pass`;
      if (t.phaseLabel === "early_bird") desc = `Early Bird · ${desc}`;
      return {
        key,
        categoryId,
        tier: ticketDisplayName(t?.type) || t.type || `Tier ${i + 1}`,
        price: Number(t.price || 0),
        availableSeats: seats,
        status: seatStatus(seats),
        desc,
        badge: coupleTicketBadge(t),
        isCoupleTicket: couple,
        isGroupTicket: group,
        personsPerUnit: ppu,
        raw: t,
      };
    });
  }, [dayPool, pickedDate]);

  const selected = ticketCards.find((t) => t.key === selectedKey) || null;

  const maxQty = useMemo(() => {
    const cap = Number(event?.maxTicketsPerUser) || 10;
    const seats = Number(selected?.availableSeats ?? 0);
    return Math.max(0, Math.min(cap, seats || cap));
  }, [event, selected]);

  const cartQty = selected && qty > 0 ? qty : 0;
  const cartTotal =
    selected && cartQty > 0 ? Number(selected.price || 0) * cartQty : 0;
  const ticketLabel =
    cartQty === 1 ? "1 Ticket" : `${cartQty || 0} Tickets`;

  useEffect(() => {
    if (qty > maxQty && maxQty > 0) setQty(maxQty);
  }, [maxQty, qty]);

  const venueLine = useMemo(() => {
    const v = event?.venue;
    if (!v) return event?.location || "—";
    if (typeof v === "string") return v;
    return [v.name, v.city].filter(Boolean).join(": ") || "—";
  }, [event]);

  const timeForPicked = useMemo(() => {
    if (!pickedDate || pickedDate === "__season__") {
      const days = event?.scheduleDays || [];
      const first = days.find(
        (d) => d?.active !== false && d?.date && (d.times || []).some(Boolean)
      );
      const t = first?.times?.find(Boolean);
      return t ? formatTimeLabel(t) : "";
    }
    const day = (event?.scheduleDays || []).find(
      (d) => String(d?.date || "").trim() === pickedDate
    );
    const t = day?.times?.find(Boolean);
    return t ? formatTimeLabel(t) : "";
  }, [event, pickedDate]);

  const metaBarRight = useMemo(() => {
    if (!pickedDate) return "";
    if (pickedDate === "__season__") {
      return `${seasonPassLabel}${timeForPicked ? ` | ${timeForPicked}` : ""}`;
    }
    return `${formatShortDay(pickedDate)}${timeForPicked ? ` | ${timeForPicked}` : ""}`;
  }, [pickedDate, seasonPassLabel, timeForPicked]);

  const buildCheckoutPath = () => {
    const params = new URLSearchParams();
    if (selected?.categoryId) params.set("tier", String(selected.categoryId));
    if (pickedDate === "__season__") params.set("pass", "season");
    else if (pickedDate) params.set("date", pickedDate);
    if (cartQty >= 1) params.set("qty", String(cartQty));
    const qs = params.toString() ? `?${params.toString()}` : "";
    return `/checkout/${id}${qs}`;
  };

  const goCheckout = () => {
    if (!selected || cartQty < 1 || selected.availableSeats <= 0) return;
    const path = buildCheckoutPath();
    if (currentUser?.token) {
      nav(path);
      return;
    }
    setAuthTab("login");
    setNextAfterAuth(path);
    setAuthOpen(true);
  };

  const onProceedStep1 = () => {
    if (!pickedDate) return;
    setSelectedKey("");
    setQty(0);
    setStep(2);
  };

  const onProceedStep2 = () => {
    goCheckout();
  };

  const dateOptionStatus = (ymd) => {
    const pool = (event?.ticketCategories || []).filter(
      (t) =>
        isTicketSaleActive(t) &&
        (String(t?.date || "").trim() === ymd ||
          ticketDateFromType(t?.type || "") === ymd)
    );
    const seats = pool.reduce((s, t) => s + Number(t.availableSeats ?? 0), 0);
    return seatStatus(seats);
  };

  const seasonStatus = () => {
    const seats = seasonPassTiers.reduce(
      (s, t) => s + Number(t.availableSeats ?? 0),
      0
    );
    return seatStatus(seasonPassTiers.length ? seats : 0);
  };

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-slate-50 dark:bg-slate-950">
        <FancyLoader />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 bg-slate-50 dark:bg-slate-950 px-4">
        <p className="text-slate-700 dark:text-slate-300">Event not found.</p>
        <button
          type="button"
          onClick={() => nav("/")}
          className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white"
        >
          Go home
        </button>
      </div>
    );
  }

  return (
    <div
      className="stb-book relative flex min-h-[100dvh] flex-col text-slate-900 dark:text-amber-50"
      style={{ fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif' }}
    >
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=Oswald:wght@500;600;700&display=swap");
        .stb-book {
          background:
            radial-gradient(1200px 500px at 10% -10%, rgba(251,191,36,0.28), transparent 55%),
            radial-gradient(900px 420px at 100% 0%, rgba(249,115,22,0.18), transparent 50%),
            linear-gradient(180deg, #fff8e7 0%, #f3f4f6 42%, #eef2f7 100%);
        }
        .dark .stb-book {
          background:
            radial-gradient(1000px 480px at 0% -5%, rgba(245,158,11,0.22), transparent 50%),
            radial-gradient(800px 400px at 100% 0%, rgba(234,88,12,0.14), transparent 45%),
            linear-gradient(180deg, #0b0b0c 0%, #12141a 55%, #0f1115 100%);
        }
        .stb-book-display { font-family: "Oswald", ui-sans-serif, system-ui, sans-serif; }
        @keyframes stbBookIn {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .stb-book-anim { animation: stbBookIn 380ms cubic-bezier(0.22,1,0.36,1) both; }
        .stb-ticket-stub {
          clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%);
        }
      `}</style>

      <header className="sticky top-0 z-30 border-b border-amber-200/80 bg-[#fff8e1]/95 backdrop-blur-md dark:border-slate-700 dark:bg-slate-950/95">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-5">
          <button
            type="button"
            onClick={() => {
              if (step === 2 && needsDateStep) setStep(1);
              else nav(`/events/${id}`);
            }}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-300/80 bg-white text-amber-700 hover:bg-amber-50 dark:border-slate-600 dark:bg-slate-900 dark:text-amber-400"
            aria-label="Back"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="15 18 9 12 15 6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            type="button"
            onClick={() => nav(`/events/${id}`)}
            className="hidden shrink-0 sm:block"
            aria-label="Show Time Bro"
          >
            <img src={logo} alt="Show Time Bro" className="h-7 w-auto object-contain" />
          </button>

          <div className="min-w-0 flex-1">
            <h1 className="stb-book-display truncate text-sm font-semibold uppercase tracking-wide text-slate-900 dark:text-white sm:text-base">
              {event.name || "Book tickets"}
            </h1>
            <p className="truncate text-[10px] font-medium text-amber-800/80 dark:text-amber-400/90 sm:text-[11px]">
              {[venueLine, metaBarRight].filter(Boolean).join(" · ") || "Booking"}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
            <button
              type="button"
              onClick={() => needsDateStep && setStep(1)}
              className={`rounded-full px-2.5 py-1.5 text-[11px] font-bold transition sm:px-3 ${
                step === 1
                  ? "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-950 shadow-sm"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400"
              }`}
            >
              1 · When
            </button>
            <button
              type="button"
              onClick={() => pickedDate && setStep(2)}
              disabled={!pickedDate && needsDateStep}
              className={`rounded-full px-2.5 py-1.5 text-[11px] font-bold transition disabled:opacity-40 sm:px-3 ${
                step === 2
                  ? "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-950 shadow-sm"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400"
              }`}
            >
              2 · Pass
            </button>
          </div>
        </div>
      </header>

      <main className="stb-book-anim mx-auto w-full max-w-3xl flex-1 px-4 py-6 sm:px-5 pb-32">
        {step === 1 && (
          <section className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-6">
            <div className="mb-4 flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-600 dark:text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
                Available
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-orange-400" />
                Fast Filling
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                Sold out
              </span>
            </div>

            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              {seasonPassTiers.length ? "Select date or season pass" : "Select Date"}
            </h2>

            <div className="mt-4 flex flex-wrap gap-3">
              {seasonPassTiers.length > 0 &&
                (() => {
                  const st = seasonStatus();
                  const sold = st === "sold";
                  const active = pickedDate === "__season__";
                  const border =
                    sold
                      ? "border-slate-200 opacity-50 cursor-not-allowed dark:border-slate-700"
                      : active
                        ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200 dark:bg-amber-950/40 dark:ring-amber-800"
                        : st === "fast"
                          ? "border-orange-400 hover:border-orange-500"
                          : "border-emerald-400 hover:border-emerald-500";
                  return (
                    <button
                      key="__season__"
                      type="button"
                      disabled={sold}
                      onClick={() => setPickedDate("__season__")}
                      className={`min-w-[9rem] rounded-xl border-2 px-4 py-3 text-left transition ${border}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-bold text-slate-900 dark:text-white">
                            Season pass
                          </div>
                          <div className="mt-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                            {seasonPassLabel}
                          </div>
                        </div>
                        <span
                          className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                            sold
                              ? "bg-slate-300"
                              : st === "fast"
                                ? "bg-orange-400"
                                : "bg-emerald-500"
                          }`}
                        />
                      </div>
                    </button>
                  );
                })()}

              {ticketDates.map((d) => {
                const st = dateOptionStatus(d);
                const sold = st === "sold";
                const active = pickedDate === d;
                const border =
                  sold
                    ? "border-slate-200 opacity-50 cursor-not-allowed dark:border-slate-700"
                    : active
                      ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200 dark:bg-amber-950/40 dark:ring-amber-800"
                      : st === "fast"
                        ? "border-orange-400 hover:border-orange-500"
                        : "border-emerald-400 hover:border-emerald-500";
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={sold}
                    onClick={() => setPickedDate(d)}
                    className={`min-w-[7.5rem] rounded-xl border-2 px-4 py-3 text-center transition ${border}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm font-bold text-slate-900 dark:text-white">
                        {formatShortDay(d)}
                      </span>
                      <span
                        className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                          sold
                            ? "bg-slate-300"
                            : st === "fast"
                              ? "bg-orange-400"
                              : "bg-emerald-500"
                        }`}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 2 && (
          <section>
            <p className="stb-book-display text-xs uppercase tracking-[0.22em] text-amber-700 dark:text-amber-400">
              Your entry
            </p>
            <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Choose your pass
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Max {Number(event?.maxTicketsPerUser) || 10} per booking.
            </p>

            <div className="mt-5 space-y-3">
              {ticketCards.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/60">
                  No passes for this selection.
                </div>
              ) : (
                ticketCards.map((t, idx) => {
                  const active = selectedKey === t.key && qty > 0;
                  const sold = t.status === "sold";
                  return (
                    <div
                      key={t.key}
                      className={`stb-book-anim relative overflow-hidden rounded-2xl border transition ${
                        active
                          ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200 shadow-sm dark:bg-amber-950/30 dark:ring-amber-800"
                          : "border-slate-200/90 bg-white dark:border-slate-700 dark:bg-slate-900"
                      }`}
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-400/10" />
                      <div className="flex items-stretch gap-0">
                        <div
                          className={`flex w-16 shrink-0 flex-col items-center justify-center border-r border-dashed px-2 py-4 ${
                            active
                              ? "border-amber-400/50 bg-amber-100/80 dark:bg-amber-900/40"
                              : "border-slate-200 bg-amber-50 dark:border-slate-700 dark:bg-amber-950/30"
                          }`}
                        >
                          <span className="stb-book-display text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400">
                            INR
                          </span>
                          <span
                            className={`stb-book-display text-center text-lg font-semibold leading-tight ${
                              active ? "text-amber-900 dark:text-amber-300" : "text-slate-900 dark:text-amber-300"
                            }`}
                          >
                            {Number(t.price || 0).toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <h3
                              className={`stb-book-display text-xl font-semibold uppercase tracking-wide ${
                                active ? "text-slate-900 dark:text-white" : "text-slate-900 dark:text-white"
                              }`}
                            >
                              {t.tier}
                            </h3>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {t.badge ? (
                                <span className="rounded-md bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                                  {t.badge}
                                </span>
                              ) : null}
                              {pickedDate === "__season__" ? (
                                <span className="rounded-md bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                                  All dates
                                </span>
                              ) : null}
                              <span
                                className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                  sold
                                    ? "bg-slate-200 text-slate-500"
                                    : t.status === "fast"
                                      ? "bg-orange-100 text-orange-800"
                                      : "bg-emerald-100 text-emerald-800"
                                }`}
                              >
                                {statusCopy(t.status)}
                              </span>
                            </div>
                            {t.desc ? (
                              <p className={`mt-2 text-xs ${active ? "text-slate-600 dark:text-amber-100/70" : "text-slate-500 dark:text-slate-400"}`}>
                                {t.desc}
                              </p>
                            ) : null}
                          </div>

                          <div className="shrink-0">
                            {sold ? (
                              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Sold out</span>
                            ) : active ? (
                              <div className="inline-flex items-center overflow-hidden rounded-full bg-amber-400 text-slate-950">
                                <button
                                  type="button"
                                  aria-label="Decrease"
                                  onClick={() => {
                                    if (qty <= 1) {
                                      setQty(0);
                                      setSelectedKey("");
                                    } else {
                                      setQty((q) => q - 1);
                                    }
                                  }}
                                  className="flex h-10 w-10 items-center justify-center text-lg font-bold hover:bg-amber-300"
                                >
                                  −
                                </button>
                                <span className="min-w-[2rem] text-center text-base font-extrabold">{qty}</span>
                                <button
                                  type="button"
                                  aria-label="Increase"
                                  disabled={qty >= maxQty}
                                  onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
                                  className="flex h-10 w-10 items-center justify-center text-lg font-bold hover:bg-amber-300 disabled:opacity-40"
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedKey(t.key);
                                  setQty(1);
                                }}
                                className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-2.5 text-sm font-extrabold text-slate-950 shadow-sm transition hover:brightness-105"
                              >
                                Grab pass
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        )}
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:px-5 sm:pb-4">
        <div className="mx-auto flex max-w-3xl items-center gap-3 overflow-hidden rounded-2xl border border-amber-200 bg-white p-3 shadow-[0_16px_40px_-20px_rgba(180,83,9,0.35)] dark:border-slate-700 dark:bg-slate-900">
          {step === 1 ? (
            <button
              type="button"
              disabled={!pickedDate}
              onClick={onProceedStep1}
              className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-3.5 text-base font-extrabold text-slate-950 disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400"
            >
              Continue to passes →
            </button>
          ) : (
            <>
              <div className="min-w-0 flex-1 pl-1">
                <p className="stb-book-display text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400">
                  {ticketLabel}
                </p>
                <p className="stb-book-display text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
                  {currencyINR(cartTotal)}
                </p>
              </div>
              <button
                type="button"
                disabled={cartQty < 1 || !selected || selected.availableSeats <= 0}
                onClick={onProceedStep2}
                className="shrink-0 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-3.5 text-sm font-extrabold text-slate-950 disabled:cursor-not-allowed disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 sm:px-8 sm:text-base"
              >
                {currentUser?.token ? "Checkout →" : "Login & book →"}
              </button>
            </>
          )}
        </div>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        next={nextAfterAuth}
        logoSrc={logo}
        brandName="Show Time Bro"
        headerVariant="logo"
      />
    </div>
  );
}
